import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorMessage } from '@/api/errors';
import type { components } from '@/api/qraft/schema';
import { AlertBox } from '@/common/AlertBox';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { AddActivityAndStatus } from '@/follow-up/AddActivityAndStatus';
import { FollowUpActivityFeed } from '@/follow-up/FollowUpActivityFeed';
import { FollowUpAssigneesRow } from '@/follow-up/FollowUpAssigneesRow';
import { FollowUpEntryContacts } from '@/follow-up/FollowUpEntryContacts';
import { FollowUpEntryStatCards } from '@/follow-up/FollowUpEntryStatCards';
import { FollowUpNotes } from '@/follow-up/FollowUpNotes';
import { FollowUpReassignDialog } from '@/follow-up/FollowUpReassignDialog';
import {
  SectionHeading,
  ViewBody,
  ViewContainer,
  ViewHeader,
  ViewHeaderTitle,
} from '@/follow-up/followUpStyles';
import { Pagination } from '@/notifications/Pagination';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { PageContainer } from '~/styles/boardStyles';
import { formatString } from '~/utils/stringFormat';

type FollowUpStatus = components['schemas']['FollowUpStatus'];

/** `follow-up-detail.php:487` — `$item_per_pge = 10`. */
const RECORDS_PER_PAGE = 10;

export const Route = createFileRoute(
  '/_app/follow-ups/entries/view/$followUpId',
)({
  component: FollowUpDetailScreen,
});

/**
 * The shared follow-up detail — `buddypress/follow-ups/follow-up-detail.php`,
 * the one 552-line template all four follow-up surfaces in the product render.
 *
 * NO CLIENT PROFILE-TYPE GATE, and that is not an oversight (spec §6.2). The
 * viewer set is PER ROW, not per profile type: `get_follow_up` admits the
 * student, the resolved partner (L7 — `requested_partner_id` while the invite
 * is pending), the coordinator, the student's LIVE study helper and an admin,
 * and answers 404 for anyone else so the endpoint is not an existence oracle.
 * The route renders `NotFoundScreen` on that 404 and that is the whole gate.
 * All four tables that link here — the two entries lists, plus the View actions
 * this group restored on `FollowUpEntriesTable` (W4.4's DIV-21) and
 * `StudentFollowUpsTable` (W4.3's DIV-12) — reach it through this one route.
 *
 * SECTIONS 5, 6 AND 7 ARE ONE GATE, NOT THREE. `:405` opens a block that closes
 * at `:552`, so the Additional Notes, the composer and the timeline stand or
 * fall together; everything above them renders for every admitted viewer. The
 * student is admitted to sections 1-4 and refused 5-7, which is why the client
 * renders off `can_view_timeline` rather than off `me.profile_type`.
 */
function FollowUpDetailScreen() {
  const { t } = useTranslation();
  const { followUpId } = Route.useParams();
  const id = Number(followUpId);
  const { me } = useCurrentUser();

  const [page, setPage] = useState(1);
  const [reassigning, setReassigning] = useState(false);

  const {
    data: detail,
    isLoading,
    isError,
  } = api.getFollowUp.useQuery({ path: { follow_up_id: id } });

  const { data: records } = api.listFollowUpRecords.useQuery(
    {
      path: { follow_up_id: id },
      query: {
        offset: (page - 1) * RECORDS_PER_PAGE,
        limit: RECORDS_PER_PAGE,
      },
    },
    { enabled: detail?.can_view_timeline === true },
  );

  const {
    mutateAsync: addRecord,
    isPending: adding,
    error: addError,
  } = api.addFollowUpRecord.useMutation();

  const handleAddRecord = async (
    content: string,
    newStatus: FollowUpStatus | null,
  ): Promise<boolean> => {
    try {
      await addRecord({
        path: { follow_up_id: id },
        body: { content, new_status: newStatus },
      });
    } catch {
      // Rendered from `addError`; the composer keeps the typed note.
      return false;
    }
    // The write moves three things a reader can see: `last_activity_at` and
    // possibly `status` on this page's stat cards, and the Last Record cell on
    // whichever entries list the viewer came from.
    setPage(1);
    await Promise.all([
      api.getFollowUp.invalidateQueries(),
      api.listFollowUpRecords.invalidateQueries(),
      api.listCoordinatorFollowUps.invalidateQueries(),
      api.listPartnerFollowUps.invalidateQueries(),
    ]);
    return true;
  };

  const handleReloadAfterReassign = async (): Promise<void> => {
    setReassigning(false);
    await Promise.all([
      api.getFollowUp.invalidateQueries(),
      // The row moves between two partners' pending queues.
      api.listPartnerPendingFollowUps.invalidateQueries(),
      api.listFollowUpRecords.invalidateQueries(),
    ]);
  };

  if (isError) return <NotFoundScreen />;
  if (isLoading || detail === undefined) return null;

  const timeline = records?.data ?? [];
  const totalRecords = records?.count ?? 0;

  return (
    <PageContainer>
      <ViewContainer>
        {/* Legacy computes the back target from the CONTEXT it was reached in
            (`:3-10`: the group tab, or the member's own profile tab). This one
            route is reached from four different lists, so the browser's own
            history is the equivalent — and the only thing that returns each of
            those four viewers to the list they came from. */}
        <LargeArrowBackButton onClick={() => window.history.back()} />
        <ViewHeader>
          <ViewHeaderTitle>
            {formatString(t('wbs.follow-up-s'), String(detail.id))}
          </ViewHeaderTitle>
        </ViewHeader>
        <ViewBody>
          <FollowUpEntryStatCards detail={detail} />
          <FollowUpAssigneesRow
            coordinator={detail.coordinator}
            studyHelper={detail.study_helper}
            viewerId={me?.id}
          />

          <SectionHeading>{t('wbs.follow-up-contacts')}</SectionHeading>
          <FollowUpEntryContacts
            detail={detail}
            viewerId={me?.id}
            onReassign={() => setReassigning(true)}
          />

          {/* `:405-552` — ONE gate over the next three blocks. */}
          {detail.can_view_timeline && (
            <>
              {/* `:409` — the whole Additional Notes block is skipped when both
                  messages are empty, and each heading is skipped when its own
                  message is. */}
              {(detail.message_for_partner ||
                detail.message_for_coordinator) && (
                <NotesWrapper>
                  <SectionHeading>
                    {t('actions.follow-up-additional-notes')}
                  </SectionHeading>
                  {detail.message_for_partner && (
                    <FollowUpNotes
                      title={t('wbs.message-from-follow-up-coordinator')}
                      message={detail.message_for_partner}
                      $smallTitle
                    />
                  )}
                  {detail.message_for_coordinator && (
                    <FollowUpNotes
                      title={t('lessons.message-from-study-helper')}
                      message={detail.message_for_coordinator}
                      $smallTitle
                    />
                  )}
                </NotesWrapper>
              )}

              <ActivitySection>
                {addError != null && (
                  <AlertBox variant="error">
                    {errorMessage(
                      addError,
                      t('wbs.there-was-a-problem-with-your-submission'),
                    )}
                  </AlertBox>
                )}
                {detail.can_add_record && (
                  <AddActivityAndStatus
                    currentUserAvatar={me?.avatar}
                    onSubmit={handleAddRecord}
                    submitting={adding}
                  />
                )}
                <FollowUpActivityFeed records={timeline} />
                {totalRecords > RECORDS_PER_PAGE && (
                  <Pagination
                    currentPage={page}
                    totalPages={Math.ceil(totalRecords / RECORDS_PER_PAGE)}
                    startIndex={(page - 1) * RECORDS_PER_PAGE}
                    endIndex={page * RECORDS_PER_PAGE}
                    totalItems={totalRecords}
                    onPageChange={setPage}
                  />
                )}
              </ActivitySection>
            </>
          )}
        </ViewBody>
      </ViewContainer>

      {reassigning && (
        <FollowUpReassignDialog
          followUpId={id}
          currentPartner={detail.partner}
          onClose={() => setReassigning(false)}
          onReloaded={() => void handleReloadAfterReassign()}
        />
      )}
    </PageContainer>
  );
}

const ActivitySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 45px;
`;

const NotesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
